/**
 * Agent Run（长程运行）types —— 切片 2 后台 Agent 波次 B 前端契约。
 *
 * 契约来源：主仓库 `docs/design/2026-08-28-可恢复事件流重连协议草案.md`（事件信封
 * 与重连语义）+ 波次 B 冻结接口契约（REST 端点与 payload 形状）。后端 A 侧同步
 * 实现中，以冻结契约为准；步骤 kind 的取值枚举后端尚未冻结，前端按原样展示。
 *
 * 事件流：`GET /agent-runs/{id}/stream?after={seq}`（SSE），data 帧统一信封
 * `{runId, seq, type, payload, occurredAt}`，命名事件 `stream.connected` /
 * `stream.resync` 为控制帧（不进领域回调），心跳为 SSE 注释行（不产生事件）。
 */

/** 运行生命周期状态（run.status / run.state 事件的 status；六态与后端
 * AgentRunStatus 对齐——queued 为创建/应答续跑后的待派发态）。 */
export type AgentRunStatus =
  | "queued"
  | "running"
  | "awaiting_input"
  | "succeeded"
  | "failed"
  | "cancelled";

/** 步骤状态（step.completed 事件的 status；running 为 step.started 派生）。 */
export type AgentRunStepStatus =
  | "running"
  | "succeeded"
  | "failed"
  | "skipped"
  | "cancelled";

/** 问答类型（run.awaiting_input 的 questionType；后端字面量为 info / authorization）。 */
export type AgentRunQuestionType = "info" | "authorization";

/** Chinese display labels for run statuses. */
export const AGENT_RUN_STATUS_LABELS: Record<AgentRunStatus, string> = {
  queued: "排队中",
  running: "执行中",
  awaiting_input: "等待应答",
  succeeded: "已完成",
  failed: "已失败",
  cancelled: "已取消",
};

/** Selectable run-status options（筛选 / 展示共用）。 */
export const AGENT_RUN_STATUS_OPTIONS: { value: AgentRunStatus; label: string }[] = (
  Object.entries(AGENT_RUN_STATUS_LABELS) as [AgentRunStatus, string][]
).map(([value, label]) => ({ value, label }));

/** 状态徽标配色（antd Tag color；详情页 / 列表页共用，禁止各页新造）。 */
export const AGENT_RUN_STATUS_TAG_COLORS: Record<AgentRunStatus, string> = {
  queued: "default",
  running: "processing",
  awaiting_input: "warning",
  succeeded: "success",
  failed: "error",
  cancelled: "default",
};

/** Chinese display labels for step statuses. */
export const AGENT_RUN_STEP_STATUS_LABELS: Record<AgentRunStepStatus, string> = {
  running: "执行中",
  succeeded: "成功",
  failed: "失败",
  skipped: "跳过",
  cancelled: "已取消",
};

/** Selectable step-status options. */
export const AGENT_RUN_STEP_STATUS_OPTIONS: { value: AgentRunStepStatus; label: string }[] = (
  Object.entries(AGENT_RUN_STEP_STATUS_LABELS) as [AgentRunStepStatus, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for question types（信息类 / 授权类）。 */
export const AGENT_RUN_QUESTION_TYPE_LABELS: Record<AgentRunQuestionType, string> = {
  info: "信息类",
  authorization: "授权类",
};

/** Selectable question-type options. */
export const AGENT_RUN_QUESTION_TYPE_OPTIONS: { value: AgentRunQuestionType; label: string }[] = (
  Object.entries(AGENT_RUN_QUESTION_TYPE_LABELS) as [AgentRunQuestionType, string][]
).map(([value, label]) => ({ value, label }));

/** 未知枚举值的展示兜底：原样透出，不臆造标签。 */
export function agentRunQuestionTypeLabel(questionType: string | null | undefined): string {
  if (!questionType) return "信息类";
  return AGENT_RUN_QUESTION_TYPE_LABELS[questionType as AgentRunQuestionType] ?? questionType;
}

/** 终态集合：进入后事件流停止、取消按钮消失。 */
export const AGENT_RUN_TERMINAL_STATUSES: readonly AgentRunStatus[] = [
  "succeeded",
  "failed",
  "cancelled",
];

export function isAgentRunTerminalStatus(status: string | null | undefined): boolean {
  return !!status && (AGENT_RUN_TERMINAL_STATUSES as readonly string[]).includes(status);
}

/** REST 快照：`GET /agent-runs/{id}` 返回的 run 详情（字段与后端
 * AgentRunDetailResponse 对齐，派生字段见各项注释；页面只消费子集）。 */
export interface AgentRun {
  id: string;
  status: AgentRunStatus;
  goal: string;
  /** 来源任务（AgentRunResponse.scheduledTaskId）：非空 = 由该定时任务触发
   * 或经单次任务定义归一创建；null = 存量手工 run（无任务定义行）。 */
  scheduledTaskId?: string | null;
  scheduledRunId?: string | null;
  /** 所属事项（AgentRunResponse.matterId；无事项 run 为 null）。 */
  matterId: string | null;
  /** resolved_context 快照（后端 AgentRunResponse.context）：任务材料区读
   *  documents（[{id,title}]，jerry 文档 ID）与 matter（{id,name} 摘要）。 */
  context?: Record<string, unknown> | null;
  steps: AgentRunStepSnapshot[];
  /** 已完成的问答记录（question 由后端回溯派生，可能为 null）。 */
  inputs: AgentRunInput[];
  // --- 预算用量 ---
  maxSteps: number;
  /** 已落库步骤数（后端 step_count；页面预算条按 steps.length 现算）。 */
  stepCount: number;
  maxWallClockMinutes: number;
  elapsedMinutes: number | null;
  maxTotalTokens: number;
  tokensUsed: number;
  // --- 终态与应答 ---
  /** run 级终态结论（后端 run.summary 列，done 收尾写入；历史 run / failed
   * 未写为 null——列空不派生）。终态摘要区快照侧据此渲染。 */
  summary: string | null;
  /** 后端序列化层派生：status=cancelled 时取 finishedAt，否则 null。 */
  cancelledAt: string | null;
  /** 取消时刻兜底（cancelledAt 缺失且 status=cancelled 时用于派生）。 */
  finishedAt?: string | null;
  failureReason: string | null;
  awaitingQuestion: string | null;
  awaitingQuestionType: AgentRunQuestionType | string | null;
  /** 应答选项（awaiting_input 时服务端给出的快捷选择）；契约未单列，容缺。 */
  awaitingOptions?: string[] | null;
  createdAt?: string;
}

/**
 * GET /agent-runs 列表项 —— 与后端 AgentRunResponse 逐字段对齐
 * （joint-common schemas.py；camelCase 序列化）。列表项不含详情扩展段
 * （steps / inputs / elapsedMinutes）。
 */
export interface AgentRunListItem {
  id: string;
  tenantId: string;
  /** 来源标识：null = 手动发起；非 null = 由该定时任务触发（任务名不在列表契约中）。 */
  scheduledTaskId: string | null;
  scheduledRunId: string | null;
  matterId: string | null;
  templateId: string | null;
  createdBy: string | null;
  goal: string;
  context: Record<string, unknown> | null;
  status: AgentRunStatus;
  failureReason: string | null;
  /** run 级终态结论（后端 run.summary 列；列空为 null，不派生）。 */
  summary: string | null;
  awaitingQuestion: string | null;
  awaitingContextRef: string | null;
  awaitingQuestionType: string;
  stepCount: number;
  maxSteps: number;
  maxWallClockMinutes: number;
  maxTotalTokens: number | null;
  tokensUsed: number;
  startedAt: string | null;
  finishedAt: string | null;
  cancelledBy: string | null;
  currentEngineTaskId: string | null;
  createdAt: string;
  updatedAt: string;
  /** 序列化层派生：status=cancelled 时取 finishedAt。 */
  cancelledAt: string | null;
}

/** REST 快照中的单步（summary/durationMs 由后端序列化层派生）。 */
export interface AgentRunStepSnapshot {
  stepIndex: number;
  /** 后端尚未冻结取值枚举，按原样展示。 */
  kind: string | null;
  skillName: string | null;
  status: AgentRunStepStatus;
  tokensUsed?: number | null;
  durationMs?: number | null;
  summary?: string | null;
}

/** 已应答的一轮问答（run.inputs[]；answer 是结构化对象，渲染用
 * agentRunAnswerText 解出文本，禁止对象直出）。 */
export interface AgentRunInput {
  question: string | null;
  answer: Record<string, unknown>;
  resumeNote?: string | null;
  answeredBy?: string | null;
  createdAt?: string | null;
}

/**
 * 结构化应答 → 展示文本：`answer.text ?? answer.choice ?? answer.note ??
 * JSON 摘要`。后端 answer 是对象（自由文本 {"text"}、选项 {"choice"}、
 * 超时备忘 {"note"}），直出会渲染成 [object Object]。
 */
export function agentRunAnswerText(answer: unknown): string {
  if (typeof answer === "string") return answer;
  if (!answer || typeof answer !== "object") return "";
  const record = answer as Record<string, unknown>;
  for (const key of ["text", "choice", "note"]) {
    const value = record[key];
    if (typeof value === "string" && value) return value;
  }
  try {
    return JSON.stringify(record);
  } catch {
    return "";
  }
}

/** `POST /agent-runs` 创建负载（201 创建即跑；AI 任务中心：单次任务定义
 * 行由后端内部补建，返回 run.scheduledTaskId 非空）。 */
export interface AgentRunCreatePayload {
  goal: string;
  templateId: string;
  matterId: string;
  /** 可选任务名（留空由服务端按目标前 30 字派生）。 */
  name?: string;
  maxSteps?: number;
  maxWallClockMinutes?: number;
  maxTotalTokens?: number;
}

/** `POST /agent-runs/{id}/input` 应答负载；answer 为结构化对象（后端
 * 契约：自由文本放 {"text"}，选项选择放 {"choice"}），不是裸字符串。 */
export interface AgentRunInputPayload {
  answer: { text: string } | { choice: string };
  resumeNote?: string;
}

// ---------------------------------------------------------------------------
// SSE 事件信封与载荷（协议 §二 / 冻结契约）
// ---------------------------------------------------------------------------

/** 领域事件统一信封（SSE data 帧 JSON）。 */
export interface AgentRunEventEnvelope<TPayload = Record<string, unknown>> {
  runId: string;
  seq: number;
  type: string;
  payload: TPayload;
  occurredAt: string;
}

/** 控制帧事件名（不进领域回调）。 */
export const AGENT_RUN_STREAM_CONNECTED_EVENT = "stream.connected";
export const AGENT_RUN_STREAM_RESYNC_EVENT = "stream.resync";
export const AGENT_RUN_STREAM_HEARTBEAT_EVENT = "stream.heartbeat";

/** `stream.connected` payload：当前最大序号 + 缓冲保留下界。 */
export interface StreamConnectedPayload {
  latestSeq: number;
  floorSeq: number;
}

/** `stream.resync` payload：缺口超出保留窗口，客户端需快照重建。 */
export interface StreamResyncPayload {
  latestSeq: number;
}

/** 领域事件类型（type 字段取值）。 */
export type AgentRunEventType =
  | "run.state"
  | "step.started"
  | "step.completed"
  | "run.awaiting_input"
  | "run.done";

/** `run.state` payload；`status` 为主读字段（= to），`from`/`to` 为调试
 * 保留（切片 2 评审 P1 后三字段并存）。 */
export interface RunStatePayload {
  status: AgentRunStatus;
  from?: string;
  to?: string;
  [key: string]: unknown;
}

/** `step.started` payload。 */
export interface StepStartedPayload {
  stepIndex: number;
  kind: string | null;
  skillName: string | null;
}

/** `step.completed` payload。 */
export interface StepCompletedPayload {
  stepIndex: number;
  status: AgentRunStepStatus;
  tokensUsed: number | null;
  durationMs: number | null;
  summary: string | null;
}

/** `run.awaiting_input` payload。 */
export interface RunAwaitingInputPayload {
  question: string;
  questionType: AgentRunQuestionType | string | null;
  options: string[] | null;
}

/** `run.done` payload（终态帧，含终态摘要）。 */
export interface RunDonePayload {
  status: AgentRunStatus;
  summary: string | null;
}
